import _ from 'lodash';
import {call, takeLatest, put} from 'redux-saga/effects';
import {delay} from 'redux-saga';

import {request} from 'utils/sagas';
import downloadFile from 'utils/downloadFile';

import {
  uploadFile,
  getCsvExport,
  getExcelExport,
  getExcelExportAsBlob
} from 'sdk/files';
import {
  UPLOAD_FILE,
  EXPORT_CSV,
  EXPORT_CSV_IN_BACKGROUND,
  exportCsvSuccess,
  exportCsvFailure,
  EXPORT_XLSX,
  EXPORT_XLSX_IN_BACKGROUND,
  exportXlsxSuccess,
  exportXlsxFailure
} from './';

import {openCsvExportModal, openXlsxExportModal} from 'ducks/modals';

function* uploadFileWorker(action) {
  const {
    file,
    signSdk,
    signData,
    startUploadingAction,
    finishUploadingAction,
    storeIdentifierAction,
    storeImageUrlAction
  } = action.payload;

  try {
    yield put(startUploadingAction());
    const signResponse = yield call(signSdk, signData);

    const {url, fields, identifier, local, imageUrl} = signResponse.data;

    let uploadData = new FormData();
    _.each(fields, (field, fieldName) => uploadData.append(fieldName, field));
    uploadData.append('file', file);
    uploadData.append('identifier', identifier);

    yield call(uploadFile, url, {formData: uploadData, local});
    yield* [
      put(storeIdentifierAction(identifier)),
      put(storeImageUrlAction(imageUrl)),
      put(finishUploadingAction(identifier))
    ];
  } catch (error) {
    yield put(finishUploadingAction());
  }
}

function* exportCsvWorker(action) {
  const {
    fileName,
    exportSdk,
    exportData,
    successExtraActions,
    failureExtraActions
  } = action.payload;

  const {data, errors} = yield request(exportSdk, exportData);

  if (data) {
    yield call(downloadFile, {data, fileName: `${fileName}.csv`});

    yield put(exportCsvSuccess(fileName));
    yield* successExtraActions.map(a => put(a()));
  }

  if (errors) {
    yield put(exportCsvFailure(fileName, errors));
    yield* failureExtraActions.map(a => put(a(errors)));
  }
}

function* exportCsvInBackgroundWorker(action) {
  const GET_CSV_EXPORT_DELAY = 3;
  const {fileName, exportSdk, exportData} = action.payload;
  const fullFileName = `${fileName}.csv`;

  yield put(
    openCsvExportModal({
      heading: `Exporting ${fullFileName}`,
      processing: true
    })
  );

  let {data: {identifier, timeLimit}, errors} = yield request(
    exportSdk,
    exportData
  );

  if (errors) {
    yield put(
      openCsvExportModal({
        heading: 'Export failed',
        processing: false,
        errors
      })
    );
    yield put(exportCsvFailure(fileName, errors));
  }

  if (identifier) {
    let i = 1;
    const limit = Math.round(timeLimit / GET_CSV_EXPORT_DELAY);
    let finishedExporting = false;

    while (i < limit) {
      let {data, errors} = yield request(getCsvExport, {identifier});

      if (errors) {
        yield put(
          openCsvExportModal({
            heading: 'Export failed',
            processing: false,
            errors
          })
        );
        yield put(exportCsvFailure(fileName, errors));
        finishedExporting = true;
        break;
      }

      if (!_.get(data, 'processing')) {
        yield call(downloadFile, {data, fileName: fullFileName});
        yield put(
          openCsvExportModal({
            heading: 'Export success',
            processing: false
          })
        );
        yield put(exportCsvSuccess(fileName));
        finishedExporting = true;
        break;
      }

      yield call(delay, GET_CSV_EXPORT_DELAY * 1000); // _.delay expects miliseconds
      i += 1;
    }

    if (!finishedExporting) {
      yield put(
        openCsvExportModal({
          heading: 'Export failed',
          processing: false,
          message: `Time limit ${timeLimit}s exceeded.`
        })
      );
      yield put(exportCsvFailure(fileName, errors));
    }
  }
}

function* exportXlsxInBackgroundWorker(action) {
  const GET_XLSX_EXPORT_DELAY = 3;
  const {fileName, exportSdk, exportData} = action.payload;
  const fullFileName = `${fileName}.xlsx`;

  yield put(
    openXlsxExportModal({
      heading: `Exporting ${fullFileName}`,
      processing: true
    })
  );

  let {data: {identifier, timeLimit}, errors} = yield request(
    exportSdk,
    exportData
  );

  if (errors) {
    yield put(
      openXlsxExportModal({
        heading: 'Export failed',
        processing: false,
        errors
      })
    );
    yield put(exportXlsxFailure(fileName, errors));
  }

  if (identifier) {
    let i = 1;
    const limit = Math.round(timeLimit / GET_XLSX_EXPORT_DELAY);
    let finishedExporting = false;

    while (i < limit) {
      let {data, errors} = yield request(getExcelExport, {identifier});

      if (errors) {
        yield put(
          openXlsxExportModal({
            heading: 'Export failed',
            processing: false,
            errors
          })
        );
        yield put(exportXlsxFailure(fileName, errors));
        finishedExporting = true;
        break;
      }

      if (!_.get(data, 'processing')) {
        let {data} = yield request(getExcelExportAsBlob, {identifier});
        yield call(downloadFile, {data, fileName: `${fileName}.xlsx`});

        yield put(
          openXlsxExportModal({
            heading: 'Export success',
            processing: false
          })
        );
        yield put(exportXlsxSuccess(fileName));
        finishedExporting = true;
        break;
      }

      yield call(delay, GET_XLSX_EXPORT_DELAY * 1000); // _.delay expects miliseconds
      i += 1;
    }

    if (!finishedExporting) {
      yield put(
        openXlsxExportModal({
          heading: 'Export failed',
          processing: false,
          message: `Time limit ${timeLimit}s exceeded.`
        })
      );
      yield put(exportXlsxFailure(fileName, errors));
    }
  }
}

function* exportXlsxWorker(action) {
  const {
    fileName,
    exportSdk,
    exportData,
    successExtraActions,
    failureExtraActions
  } = action.payload;

  const {data, errors} = yield request(exportSdk, exportData);

  if (data) {
    yield call(downloadFile, {data, fileName: `${fileName}.xlsx`});

    yield put(exportXlsxSuccess(fileName));
    yield* successExtraActions.map(a => put(a()));
  }

  if (errors) {
    yield put(exportXlsxFailure(fileName, errors));
    yield* failureExtraActions.map(a => put(a(errors)));
  }
}

export function* filesSaga() {
  yield takeLatest(UPLOAD_FILE, uploadFileWorker);
  yield takeLatest(EXPORT_CSV, exportCsvWorker);
  yield takeLatest(EXPORT_CSV_IN_BACKGROUND, exportCsvInBackgroundWorker);
  yield takeLatest(EXPORT_XLSX_IN_BACKGROUND, exportXlsxInBackgroundWorker);
  yield takeLatest(EXPORT_XLSX, exportXlsxWorker);
}

export default filesSaga;
